import type { BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import type { MenuItem, PoiContentSlide } from '@/types/shop';
import type { FnbFilterConfig } from '@/stores/types/fnbStore';
import poiUtils from '@/utils/poi';
import { getPoiListImageUrlFromContent } from '@/utils/poi/poiImage';
import { SHOP_SEARCH_MODE } from '@/enums/shop';

export const convertPoiContentToSlide = (lang: string, buildings: BuildingInStoreInfo[], searchText?: string): PoiContentSlide[] => {
    const allPois = buildings.flatMap((building) =>
        (building.filterPoiContents.fnb ?? []).map((poi) => ({
            poi,
            building,
        }))
    );

    return allPois.map(({ poi, building }) => {
        const contents = poi.contents;

        const name = poi.name?.[lang] ? poi.name?.[lang] : poi.name?.['ko'];
        const fnbUrl = getPoiListImageUrlFromContent(poi, lang);
        const buildingName = building?.name?.[lang] ? building?.name?.[lang] : building?.name?.['ko'];
        const floor = poiUtils.resolveFloorSlideLabel(
            contents.cstrFlrNm?.[lang] || poiUtils.getFloorName(lang, poi.studioFloorId, building.floors ?? [])
        );
        const foundCategory = building.filterCategories.fnb.find((c) => c.clientCode === poi.contents.smclsCd?.[lang]);

        // Extract matching keyword if searchText is provided
        const matchingKeyword = searchText ? poiUtils.extractMatchingKeyword(contents.crltSrchKwrd || {}, searchText, lang) : undefined;

        return {
            id: poi.id,
            name,
            keyword: matchingKeyword,
            buildingName,
            floor,
            url: fnbUrl,
            data: poi,
            poiType: 'fnb',
            category: foundCategory ?? undefined,
        };
    });
};

export const filterByFloor = (fnb: PoiContentSlide, config: FnbFilterConfig): boolean => {
    const { selectedBuildingId, storeCode, buildingCode, selectedFloor, buildings } = config;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = fnb.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = fnb.data.contents.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrCd !== storeCode || poiCstrTownCd !== buildingCode) {
            return false;
        }

        // Now filter by floor using cstrFlrNm instead of studioFloorId
        if (selectedFloor?.id === 'all') {
            return true;
        }

        // Find building from storeCode and buildingCode to get floors
        const building = buildings?.find((b) => b.storeCode === storeCode && b.buildingCode === buildingCode);
        if (!building) return false;

        // Get floor name from POI content
        const poiFloorName = fnb.data.contents.cstrFlrNm?.ko || poiUtils.getFloorName('ko', fnb?.data?.studioFloorId, building.floors) || '';
        // Get selected floor name
        const selectedFloorInfo = selectedFloor?.data as FloorInfo;
        if (!selectedFloorInfo) return false;

        const selectedFloorName = selectedFloorInfo.name?.ko || '';

        // Compare floor names
        return poiFloorName === selectedFloorName;
    }


    // Case: all buildings mode
    if (selectedBuildingId === 'all') {
        if (selectedFloor?.id === 'all') return true;

        if (Array.isArray(selectedFloor?.data)) {
            return (selectedFloor.data as string[]).includes(fnb.data.studioFloorId);
        }

        return false;
    }

    return false;
};

export const filterByCategory = (fnb: PoiContentSlide, config: FnbFilterConfig): boolean => {
    const { selectedCategory, storeCode, buildingCode, selectedBuildingId, buildings } = config;

    if (!selectedCategory) return false;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = fnb.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = fnb.data.contents.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        // Fallback: use selectedBuildingId if storeCode/buildingCode not available
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (fnb.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (selectedCategory.clientCode === 'all') {
        return true;
    }

    const building = buildings?.find((b) => b.studioBuildingId === fnb.data.studioBuildingId);

    if (selectedCategory.categoryCode) {
        return fnb.data.categories.includes(selectedCategory.categoryCode);
    } else {
        const foundCategory = building?.buildingCategories.find((c) => c.clientCode === selectedCategory.clientCode);
        return fnb.data.categories.includes(foundCategory?.categoryCode || '');
    }
};

export const filterByText = (fnb: PoiContentSlide, config: FnbFilterConfig, keyboardLanguage: 'ko' | 'en'): boolean => {
    const { searchText, language, storeCode, buildingCode, selectedBuildingId, buildings } = config;

    // Filter by storeCode and buildingCode using cstrCd.ko and cstrTownCd.ko
    if (storeCode != null && buildingCode != null) {
        const poiCstrCd = fnb.data.contents.cstrCd?.ko || '';
        const poiCstrTownCd = fnb.data.contents.cstrTownCd?.ko || '';

        // Filter by storeCode (cstrTownCd.ko) and buildingCode (cstrCd.ko)
        if (poiCstrTownCd !== buildingCode || poiCstrCd !== storeCode) {
            return false;
        }
    } else if (selectedBuildingId && selectedBuildingId !== 'all') {
        // Fallback: use selectedBuildingId if storeCode/buildingCode not available
        const building = buildings?.find((b) => b.id === selectedBuildingId);
        if (!building) return false;
        if (fnb.data.studioBuildingId !== building.studioBuildingId) {
            return false;
        }
    }

    if (!searchText?.trim()) return true;

    const nameKeyboard = fnb.data.name[keyboardLanguage] || '';
    const keywordKeyboard = (fnb.data.contents.crltSrchKwrd['ko'] || '').replaceAll('#', '');
    const nameDefault = fnb.data.name[language] || '';
    const keywordDefault = (fnb.data.contents.crltSrchKwrd['ko'] || '').replaceAll('#', '');
    const normalizedSearchText = searchText.trim();

    return (
        poiUtils.searchByText(nameKeyboard, normalizedSearchText) ||
        poiUtils.searchByText(keywordKeyboard, normalizedSearchText) ||
        poiUtils.searchByText(nameDefault, normalizedSearchText) ||
        poiUtils.searchByText(keywordDefault, normalizedSearchText)
    );
};

export const createFnbFilter = (searchMode: SHOP_SEARCH_MODE, config: FnbFilterConfig, keyboardLanguage: 'ko' | 'en') => {
    const filterMap = {
        [SHOP_SEARCH_MODE.SEARCH_BY_FLOOR]: filterByFloor,
        [SHOP_SEARCH_MODE.SEARCH_BY_CATEGORY]: filterByCategory,
        [SHOP_SEARCH_MODE.SEARCH_BY_TEXT]: filterByText,
    };

    const filterFunction = filterMap[searchMode];
    return (fnb: PoiContentSlide) => {
        return filterFunction ? filterFunction(fnb, config, keyboardLanguage) : true;
    };
};

export const filterInvalidMenuList = (arr: MenuItem[]) => {
    return arr.filter((obj) => Object.values(obj).every((v) => v !== null && v !== '-'));
};

const fnbUtils = {
    convertPoiContentToSlide,
    filterByFloor,
    filterByCategory,
    filterByText,
    createFnbFilter,
    filterInvalidMenuList,
};

export default fnbUtils;
